/* =====================================================================
   Never Not New York — shared behaviors for custom nnny-* sections.
   Loaded as a module, so it executes once no matter how many sections
   include it. Re-runs initializers on Shopify theme editor events.
   ===================================================================== */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- scroll reveal ---------- */
function initReveal(scope) {
  const els = scope.querySelectorAll('[data-nnny-reveal]:not(.is-visible), [data-nnny-stagger]:not(.is-visible)');
  if (!els.length) return;
  if (!('IntersectionObserver' in window) || reduceMotion) {
    els.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );
  els.forEach((el) => io.observe(el));
}

/* ---------- carousels: arrows + drag to scroll ---------- */
function initCarousels(scope) {
  scope.querySelectorAll('[data-nnny-carousel-btn]').forEach((btn) => {
    if (btn.dataset.nnnyBound) return;
    btn.dataset.nnnyBound = 'true';
    btn.addEventListener('click', () => {
      const track = document.getElementById(btn.dataset.nnnyCarouselBtn);
      if (!track) return;
      const dir = parseInt(btn.dataset.dir || '1', 10);
      const card = track.querySelector(':scope > *');
      const step = card ? card.offsetWidth + 20 : Math.round(track.clientWidth * 0.8);
      track.scrollBy({ left: dir * step, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  });

  scope.querySelectorAll('.nnny-track').forEach((track) => {
    if (track.dataset.nnnyDrag) return;
    track.dataset.nnnyDrag = 'true';
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      track.setPointerCapture(e.pointerId);
    });
    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) {
        moved = true;
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
    });
    const end = () => {
      down = false;
      track.classList.remove('is-dragging');
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('pointerleave', end);
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
    // keyboard support on focusable tracks
    if (track.hasAttribute('tabindex')) {
      track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const card = track.querySelector(':scope > *');
        const step = card ? card.offsetWidth + 20 : 300;
        track.scrollBy({ left: (e.key === 'ArrowRight' ? 1 : -1) * step, behavior: 'smooth' });
      });
    }
  });
}

/* ---------- rotating announcement bar ---------- */
function initAnnouncements(scope) {
  scope.querySelectorAll('[data-nnny-announcement]').forEach((root) => {
    if (root.dataset.nnnyBound) return;
    root.dataset.nnnyBound = 'true';
    const msgEl = root.querySelector('[data-nnny-announcement-msg]');
    const msgs = JSON.parse(root.dataset.nnnyMessages || '[]');
    if (!msgEl || msgs.length < 2) return;
    const interval = parseInt(root.dataset.nnnyInterval || '4500', 10);
    let i = 0;
    let timer = null;
    msgEl.style.transition = 'opacity .3s ease';
    const set = (n) => {
      i = (n + msgs.length) % msgs.length;
      msgEl.style.opacity = 0;
      setTimeout(() => {
        msgEl.textContent = msgs[i];
        msgEl.style.opacity = 1;
      }, 200);
    };
    const play = () => {
      if (reduceMotion) return;
      timer = setInterval(() => set(i + 1), interval);
    };
    const reset = () => {
      clearInterval(timer);
      play();
    };
    root.querySelector('[data-nnny-announcement-prev]')?.addEventListener('click', () => {
      set(i - 1);
      reset();
    });
    root.querySelector('[data-nnny-announcement-next]')?.addEventListener('click', () => {
      set(i + 1);
      reset();
    });
    play();
  });
}

/* ---------- FAQ tabs + accordion ---------- */
function initFaq(scope) {
  scope.querySelectorAll('[data-nnny-faq]').forEach((root) => {
    if (root.dataset.nnnyBound) return;
    root.dataset.nnnyBound = 'true';
    root.querySelectorAll('.nnny-faq-q').forEach((q) => {
      q.addEventListener('click', () => {
        const row = q.closest('.nnny-faq-row');
        const open = row.classList.toggle('open');
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
    root.querySelectorAll('[data-nnny-faq-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        root.querySelectorAll('[data-nnny-faq-tab]').forEach((t) => t.removeAttribute('aria-current'));
        tab.setAttribute('aria-current', 'true');
        const cat = tab.dataset.nnnyFaqTab;
        root.querySelectorAll('.nnny-faq-row').forEach((row) => {
          const show = cat === 'all' || row.dataset.cat === cat;
          row.style.display = show ? '' : 'none';
          row.classList.remove('open');
          row.querySelector('.nnny-faq-q')?.setAttribute('aria-expanded', 'false');
        });
      });
    });
  });
}

/* ---------- drawers (filter/sort) ---------- */
function initDrawers(scope) {
  scope.querySelectorAll('[data-nnny-drawer]').forEach((drawer) => {
    if (drawer.dataset.nnnyBound) return;
    drawer.dataset.nnnyBound = 'true';
    const panel = drawer.querySelector('.nnny-drawer-panel');
    const opener = document.querySelector('[data-nnny-drawer-open="' + drawer.id + '"]');
    const open = () => {
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      if (reduceMotion || !panel) {
        drawer.hidden = true;
        return;
      }
      const done = () => {
        drawer.hidden = true;
        panel.removeEventListener('transitionend', done);
      };
      panel.addEventListener('transitionend', done);
    };
    opener?.addEventListener('click', open);
    drawer.querySelectorAll('[data-nnny-drawer-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });
    drawer.addEventListener('nnny:drawer-close', close);
  });
}

/* ---------- collection filter/sort drawer -> URL params ---------- */
function initCollectionControls(scope) {
  scope.querySelectorAll('[data-nnny-collection-controls]').forEach((root) => {
    if (root.dataset.nnnyBound) return;
    root.dataset.nnnyBound = 'true';
    root.querySelector('[data-nnny-apply]')?.addEventListener('click', () => {
      const url = new URL(window.location.href);
      const sort = root.querySelector('input[name="nnny-sort"]:checked');
      if (sort && sort.value) url.searchParams.set('sort_by', sort.value);
      else url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      root.querySelectorAll('[data-nnny-filter-param]').forEach((group) => {
        const param = group.dataset.nnnyFilterParam;
        url.searchParams.delete(param);
        group.querySelectorAll('input:checked').forEach((input) => {
          url.searchParams.append(param, input.value);
        });
      });
      window.location.href = url.toString();
    });
    root.querySelector('[data-nnny-clear]')?.addEventListener('click', () => {
      const url = new URL(window.location.href);
      window.location.href = url.pathname;
    });
  });
}

/* ---------- PDP: gallery, quantity, variants, buy now ---------- */
function initPdp(scope) {
  scope.querySelectorAll('[data-nnny-pdp]').forEach((root) => {
    if (root.dataset.nnnyBound) return;
    root.dataset.nnnyBound = 'true';

    // gallery
    const main = root.querySelector('[data-nnny-pdp-main]');
    root.querySelectorAll('[data-nnny-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => {
        root.querySelectorAll('[data-nnny-thumb]').forEach((t) => t.classList.remove('is-active'));
        btn.classList.add('is-active');
        if (main) {
          main.src = btn.dataset.nnnyThumb;
          if (btn.dataset.nnnySrcset) main.srcset = btn.dataset.nnnySrcset;
          else main.removeAttribute('srcset');
        }
      });
    });

    // quantity
    const qtyEl = root.querySelector('[data-nnny-qty-value]');
    const qtyInput = root.querySelector('input[name="quantity"]');
    root.querySelectorAll('[data-nnny-qty-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const delta = parseInt(btn.dataset.nnnyQtyBtn, 10);
        const current = parseInt(qtyInput?.value || '1', 10);
        const next = Math.max(1, current + delta);
        if (qtyInput) qtyInput.value = String(next);
        if (qtyEl) qtyEl.textContent = String(next);
      });
    });

    // variants
    const variantData = root.querySelector('[data-nnny-variants]');
    const variants = variantData ? JSON.parse(variantData.textContent) : [];
    const idInput = root.querySelector('input[name="id"]');
    const priceEl = root.querySelector('[data-nnny-pdp-price]');
    const addBtn = root.querySelector('[data-nnny-pdp-add]');
    const money = (cents) => {
      const format = root.dataset.nnnyMoneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    };
    function currentSelection() {
      const opts = [];
      root.querySelectorAll('[data-nnny-option-index]').forEach((group) => {
        const checked = group.querySelector('input:checked');
        opts[parseInt(group.dataset.nnnyOptionIndex, 10)] = checked ? checked.value : null;
      });
      return opts;
    }
    function resolveVariant() {
      const sel = currentSelection();
      if (!sel.length) return null;
      return variants.find((v) => sel.every((val, idx) => val === null || v.options[idx] === val));
    }
    root.querySelectorAll('[data-nnny-option-index] input').forEach((input) => {
      input.addEventListener('change', () => {
        const v = resolveVariant();
        if (!v) return;
        if (idInput) idInput.value = String(v.id);
        if (priceEl) {
          priceEl.innerHTML =
            money(v.price) +
            (v.compare_at_price && v.compare_at_price > v.price ? ' <s>' + money(v.compare_at_price) + '</s>' : '');
        }
        if (addBtn) {
          addBtn.disabled = !v.available;
          const label = addBtn.querySelector('[data-nnny-add-label]');
          if (label) label.textContent = v.available ? addBtn.dataset.nnnyAddText : addBtn.dataset.nnnySoldoutText;
        }
      });
    });

    // buy it now: add current selection then go to checkout
    root.querySelector('[data-nnny-buy-now]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true;
      try {
        const body = new FormData();
        body.append('id', idInput ? idInput.value : '');
        body.append('quantity', qtyInput ? qtyInput.value : '1');
        const res = await fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          body,
        });
        if (!res.ok) throw new Error('add failed');
        window.location.href = '/checkout';
      } catch (err) {
        btn.disabled = false;
        window.location.href = '/cart';
      }
    });
  });
}

function initAll(scope) {
  const root = scope || document;
  initReveal(root);
  initCarousels(root);
  initAnnouncements(root);
  initFaq(root);
  initDrawers(root);
  initCollectionControls(root);
  initPdp(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document));
} else {
  initAll(document);
}

/* re-init inside the theme editor when sections are re-rendered */
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
